// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 9d371f70-1e1f-4085-a7ca-666c2ec62fe8

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select,
  FormControl, InputLabel, Alert, Tooltip, CircularProgress, Divider,
  Accordion, AccordionSummary, AccordionDetails, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpBubble from './HelpBubble';

// Installation guides for a product. Frank's definition, 2026-08-19: "a
// correlation of notes, diagrams and vendor information on installation, mostly
// in the cloud."
//
// NOT called runbooks. Optimum uses that word for the cutover script of a
// migration weekend -- the ordered steps, who does what, when to stop -- and
// letting installation knowledge take it would mean the cutover feature, when
// it is built, has nowhere left to call itself what it is. 036 renamed the
// table for the same reason: a screen title saying one thing over a table
// saying another leaves the wrong word in every query and log line.
//
// This screen is master-only for the same reason the rest of the catalog is,
// and the reason is worth stating on the screen itself rather than only in the
// schema: an entry here is read while working EVERY other client, so a client's
// hostname or a named person landing in one of these is a disclosure to every
// engagement that follows. 033_catalog_runbook.sql makes the de-identification
// claim a stored column with a name against it; this UI's job is to make that
// claim impossible to make accidentally, which is why the dialog says so in
// words next to the save button rather than hiding it in a tooltip.
//
// The kinds mirror the table's CHECK constraint. Same trap as
// ALLOWED_FIELDS/DISCOVERY_FIELD_KEYS in CLAUDE.md: a kind added on one side
// only is a dead option that fails on save, so both move together.
const KINDS = [
  { key: 'install', label: 'Installation', hint: 'How to stand it up from nothing.' },
  { key: 'config', label: 'Configuration', hint: 'How it is set up once it exists.' },
  { key: 'script', label: 'Script', hint: 'Something runnable, kept verbatim.' },
  { key: 'lesson', label: 'Lesson learned', hint: 'What went wrong, so it goes wrong once.' },
  { key: 'gotcha', label: 'Gotcha', hint: 'A trap in the product itself.' },
];
const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.key, k.label]));

const EMPTY = { id: null, catalogAppId: '', title: '', body: '', kind: 'config', sourceEngagement: '', vendorDocUrl: '' };

export default function CatalogInstallGuides({ api, entries }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api('/catalog/install-guides'));
      setErr('');
    } catch (e) {
      setErr(e.message || 'could not load installation guides');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const appName = useCallback(
    (id) => (entries.find((e) => e.id === id) || {}).name || '(unknown application)',
    [entries]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => `${r.title} ${r.body} ${appName(r.catalogAppId)}`
      .toLowerCase().includes(needle));
  }, [rows, q, appName]);

  // Grouped by application, because the question a person arrives with is
  // "what do we know about running Citrix", not "show me every lesson".
  const grouped = useMemo(() => {
    const m = new Map();
    filtered.forEach((r) => {
      if (!m.has(r.catalogAppId)) m.set(r.catalogAppId, []);
      m.get(r.catalogAppId).push(r);
    });
    return [...m.entries()]
      .map(([id, list]) => ({ id, name: appName(id), list }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered, appName]);

  async function save() {
    setSaving(true);
    try {
      await api('/catalog/install-guides', { method: 'PUT', body: JSON.stringify(editor) });
      setMsg(editor.id ? 'Installation guide updated.' : 'Installation guide added.');
      setEditor(null);
      await load();
    } catch (e) {
      setErr(e.message || 'could not save');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    try {
      await api(`/catalog/install-guides?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      setConfirmDelete(null);
      setMsg('Installation guide deleted.');
      await load();
    } catch (e) {
      setErr(e.message || 'could not delete');
    }
  }

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Installation guides</Typography>
        <HelpBubble topic="catalogInstallGuides" />
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" startIcon={<AddIcon />}
          disabled={!entries.length}
          onClick={() => setEditor({ ...EMPTY, catalogAppId: entries[0] ? entries[0].id : '' })}>
          Add entry
        </Button>
      </Stack>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, maxWidth: 780 }}>
        Notes, diagrams, scripts and vendor documentation for standing a product up,
        mostly in the cloud. A guide describes the PRODUCT, not any one client, and is
        read while working every other engagement. Nothing here may carry a
        client&apos;s hostnames, paths or people.
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <TextField size="small" fullWidth value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search guides by title, content or application"
        sx={{ mb: 2 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

      {!grouped.length && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {rows.length ? 'Nothing matches that search.'
              : 'No installation guides yet. Add the notes, diagrams, scripts and vendor documentation worth keeping.'}
          </Typography>
        </Paper>
      )}

      {grouped.map((g) => (
        <Accordion key={g.id} defaultExpanded disableGutters
          sx={{ mb: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>{g.name}</Typography>
            <Chip size="small" label={g.list.length} sx={{ ml: 1 }} />
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={1}>
              {g.list.map((r) => (
                <Paper key={r.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Chip size="small" label={KIND_LABEL[r.kind] || r.kind} />
                        <Typography sx={{ fontWeight: 600 }}>{r.title}</Typography>
                      </Stack>
                      <Typography variant="body2" component="pre"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          fontFamily: 'inherit', m: 0, color: 'text.secondary' }}>
                        {r.body}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      {/* The de-identification claim, shown rather than stored
                          silently: a reader deciding whether to trust an entry
                          on a client call needs to see who vouched for it. */}
                      {r.vendorDocUrl && (
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          <a href={r.vendorDocUrl} target="_blank" rel="noreferrer">vendor documentation</a>
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {r.sourceEngagement ? `From ${r.sourceEngagement}. ` : ''}
                        De-identified by {r.deIdentifiedBy}
                        {r.deIdentifiedAt ? ` on ${String(r.deIdentifiedAt).slice(0, 10)}` : ''}.
                      </Typography>
                    </Box>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditor({
                        id: r.id, catalogAppId: r.catalogAppId, title: r.title,
                        body: r.body, kind: r.kind, sourceEngagement: r.sourceEngagement || '',
                        vendorDocUrl: r.vendorDocUrl || '',
                      })}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setConfirmDelete(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      <Dialog open={!!editor} onClose={() => !saving && setEditor(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editor && editor.id ? 'Edit installation guide' : 'Add installation guide'}
        </DialogTitle>
        <DialogContent dividers>
          {editor && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Application</InputLabel>
                <Select label="Application" value={editor.catalogAppId}
                  onChange={(e) => setEditor({ ...editor, catalogAppId: e.target.value })}>
                  {entries.map((en) => <MenuItem key={en.id} value={en.id}>{en.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Kind</InputLabel>
                <Select label="Kind" value={editor.kind}
                  onChange={(e) => setEditor({ ...editor, kind: e.target.value })}>
                  {KINDS.map((k) => (
                    <MenuItem key={k.key} value={k.key}>{k.label} — {k.hint}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" fullWidth label="Title" value={editor.title}
                onChange={(e) => setEditor({ ...editor, title: e.target.value })} />
              <TextField size="small" fullWidth multiline minRows={10} label="Content"
                value={editor.body}
                onChange={(e) => setEditor({ ...editor, body: e.target.value })} />
              {/* A guide is notes PLUS diagrams PLUS the vendor's own
                  documentation. Without a field for the last of those, links
                  get pasted into the body where nothing can find them. */}
              <TextField size="small" fullWidth
                label="Vendor documentation link (optional)"
                placeholder="https://docs.vendor.com/install"
                value={editor.vendorDocUrl || ''}
                onChange={(e) => setEditor({ ...editor, vendorDocUrl: e.target.value })} />
              <TextField size="small" fullWidth
                label="Where this came from (optional)"
                placeholder="e.g. an Epic ODB build, March 2025"
                helperText="Words, not a client name. This field is read by everyone who sees the entry."
                value={editor.sourceEngagement}
                onChange={(e) => setEditor({ ...editor, sourceEngagement: e.target.value })} />
              {/* Stated at the point of saving, deliberately. The rule is easy
                  to hold for three entries and easy to forget on the fourth,
                  and the person clicking Save is the one making the claim. */}
              <Alert severity="warning">
                Saving records <strong>you</strong> as having checked this entry carries no
                client&apos;s identifying detail: no hostnames, IP addresses, file paths,
                URLs into a client&apos;s systems, or named people. It will be read while
                working every other client.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save}
            disabled={saving || !editor || !editor.catalogAppId
              || !editor.title.trim() || !editor.body.trim()}>
            {saving ? 'Saving…' : 'Save entry'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete this installation guide?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            &ldquo;{confirmDelete && confirmDelete.title}&rdquo; will be removed. Installation
            guides are not soft-deleted, so this cannot be undone from the app.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained"
            onClick={() => remove(confirmDelete.id)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
